import React, { useState, useEffect } from "react";
import AdminLayout from "./AdminLayout";
import { API_BASE_URL } from "./apiConfig";

const SP_API = `${API_BASE_URL}/awbsp`;

const CreateBillWithSp = () => {
  const [activeTab, setActiveTab] = useState("entry");
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  const [awbData, setAwbData] = useState([]);
  const [isFetching, setIsFetching] = useState(true);

  const [formData, setFormData] = useState({
    Client_ID: 1,
    Company_Code: 1,
    Branch_Code: 1,
    User_ID: 1,
    AWB_NO: "",
    AWB_Dt: new Date().toISOString().split("T")[0],
    AC_Code: "",
    Qty: "",
    Weight: "",
    Remarks: "",
  });

  // Load records via the stored-procedure GET endpoint
  const loadRecords = async () => {
    setIsFetching(true);
    try {
      const res = await fetch(SP_API);
      if (!res.ok) throw new Error("Failed to fetch AWB records (SP).");
      const data = await res.json();
      setAwbData(data);
    } catch (err) {
      console.error("Load Error:", err);
      setMessage({ type: "error", text: err.message });
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    loadRecords();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const processedValue = [
      "Client_ID",
      "Company_Code",
      "Branch_Code",
      "User_ID",
      "AC_Code",
      "Qty",
    ].includes(name)
      ? parseInt(value) || ""
      : name === "Weight"
        ? parseFloat(value) || ""
        : value;

    setFormData((prev) => ({ ...prev, [name]: processedValue }));
  };

  // Insert via the stored-procedure POST endpoint
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage({ type: "", text: "" });

    try {
      const response = await fetch(SP_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
            `Server Error: ${response.status} ${response.statusText}`,
        );
      }

      setMessage({
        type: "success",
        text: "AWB Bill Created Successfully via Stored Procedure!",
      });

      setFormData((prev) => ({
        ...prev,
        AWB_NO: "",
        AC_Code: "",
        Qty: "",
        Weight: "",
        Remarks: "",
        AWB_Dt: new Date().toISOString().split("T")[0],
      }));

      // Refresh the list so the new record shows up immediately
      loadRecords();
    } catch (err) {
      console.error("API POST Error:", err);
      setMessage({
        type: "error",
        text:
          err.message === "Failed to fetch"
            ? "Network error: check the backend is running and reachable."
            : err.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AdminLayout awbData={awbData}>
      <style>{`
        .page-header { margin-bottom: 24px; }
        .page-title { font-size: 1.5rem; color: #0f172a; font-weight: 700; margin: 0; }
        .content-card { background: #ffffff; border-radius: 12px; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05); border: 1px solid #f1f5f9; overflow: hidden; }
        .tab-container { display: flex; background: #f8fafc; border-bottom: 1px solid #e2e8f0; padding: 0 16px; }
        .tab-btn { padding: 16px 24px; background: transparent; border: none; border-bottom: 2px solid transparent; font-weight: 600; font-size: 0.9rem; color: #64748b; cursor: pointer; transition: all 0.2s ease; }
        .tab-btn:hover { color: #0f172a; }
        .tab-btn.active { color: #2563eb; border-bottom-color: #2563eb; }
        .form-wrapper { padding: 32px; }
        .form-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 24px; }
        .form-group { display: flex; flex-direction: column; gap: 8px; }
        .form-group label { font-size: 0.875rem; font-weight: 600; color: #475569; }
        .form-control { width: 100%; padding: 10px 14px; border: 1px solid #cbd5e1; border-radius: 8px; font-size: 0.95rem; color: #1e293b; box-sizing: border-box; }
        .form-control:focus { outline: none; border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15); }
        .btn-submit { margin-top: 32px; padding: 12px 28px; background: #2563eb; color: white; border: none; border-radius: 8px; font-weight: 600; font-size: 0.95rem; cursor: pointer; }
        .btn-submit:hover:not(:disabled) { background: #1d4ed8; }
        .btn-submit:disabled { opacity: 0.7; cursor: not-allowed; }
        .alert { padding: 12px 16px; border-radius: 8px; margin-bottom: 24px; font-weight: 500; font-size: 0.9rem; }
        .alert-success { background: #ecfdf5; color: #065f46; border: 1px solid #a7f3d0; }
        .alert-error { background: #fef2f2; color: #991b1b; border: 1px solid #fecaca; }
        .table-wrap { overflow-x: auto; width: 100%; }
        .data-table { width: 100%; border-collapse: collapse; text-align: left; }
        .data-table th { background: #f8fafc; padding: 14px 24px; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.05em; color: #64748b; font-weight: 700; border-bottom: 1px solid #e2e8f0; }
        .data-table td { padding: 16px 24px; font-size: 0.9rem; color: #334155; border-bottom: 1px solid #e2e8f0; }
        .data-table tbody tr:hover { background: #f1f5f9; }
        .empty-state { text-align: center; padding: 48px 24px; color: #64748b; }
      `}</style>

      <div className="page-header">
        <h1 className="page-title">AWB Management (Stored Procedure)... 35</h1>
      </div>

      <div className="content-card">
        <div className="tab-container">
          <button
            className={`tab-btn ${activeTab === "entry" ? "active" : ""}`}
            onClick={() => setActiveTab("entry")}
          >
            Create New Entry (SP)
          </button>
          <button
            className={`tab-btn ${activeTab === "view" ? "active" : ""}`}
            onClick={() => setActiveTab("view")}
          >
            View Records ({awbData.length})
          </button>
        </div>

        {activeTab === "entry" && (
          <div className="form-wrapper">
            {message.text && (
              <div
                className={`alert ${message.type === "success" ? "alert-success" : "alert-error"}`}
              >
                {message.text}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group">
                  <label>AWB Number *</label>
                  <input
                    name="AWB_NO"
                    className="form-control"
                    placeholder="e.g., 12345678"
                    value={formData.AWB_NO}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Date *</label>
                  <input
                    type="date"
                    name="AWB_Dt"
                    className="form-control"
                    value={formData.AWB_Dt}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>AC Code *</label>
                  <input
                    type="number"
                    name="AC_Code"
                    className="form-control"
                    placeholder="e.g., 1001"
                    value={formData.AC_Code}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Quantity</label>
                  <input
                    type="number"
                    name="Qty"
                    className="form-control"
                    placeholder="Number of items"
                    value={formData.Qty}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>Weight (kg)</label>
                  <input
                    type="number"
                    step="0.001"
                    name="Weight"
                    className="form-control"
                    placeholder="0.000"
                    value={formData.Weight}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>Remarks</label>
                  <input
                    name="Remarks"
                    className="form-control"
                    placeholder="Any additional notes..."
                    value={formData.Remarks}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <button type="submit" className="btn-submit" disabled={isLoading}>
                {isLoading ? "Saving Record..." : "Save AWB Record (via SP)"}
              </button>
            </form>
          </div>
        )}

        {activeTab === "view" && (
          <div className="table-wrap">
            {isFetching ? (
              <div className="empty-state">Loading records...</div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>AWB No</th>
                    <th>Date</th>
                    <th>AC Code</th>
                    <th>Qty</th>
                    <th>Weight</th>
                    <th>Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {awbData.length > 0 ? (
                    awbData.map((item, idx) => (
                      <tr key={item.id || idx}>
                        <td>
                          <strong>{item.awB_NO || item.AWB_NO || "-"}</strong>
                        </td>
                        <td>
                          {item.awB_Dt?.split("T")[0] ||
                            item.AWB_Dt?.split("T")[0] ||
                            "-"}
                        </td>
                        <td>{item.aC_Code || item.AC_Code || "-"}</td>
                        <td>{item.qty || item.Qty || "-"}</td>
                        <td>
                          {item.weight || item.Weight
                            ? `${item.weight || item.Weight} kg`
                            : "-"}
                        </td>
                        <td>{item.remarks || item.Remarks || "-"}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="empty-state">
                        <div style={{ marginBottom: "8px", fontSize: "1.2rem" }}>
                          📦
                        </div>
                        No AWB records found in the database.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default CreateBillWithSp;